import { yupResolver } from '@hookform/resolvers/yup';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutlined,
  ScienceOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Slide,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services';
import { ResearchActivityType } from '@avanzu/types';

interface ProjectFormData {
  projectName: string;
  taxYear: string;
}

interface ActivityFormData {
  activityName: string;
  activityType: ResearchActivityType;
}

const projectSchema = yup.object({
  projectName: yup.string().required('Project name is required'),
  taxYear: yup.string().required('Tax year is required'),
});

const activitySchema = yup.object({
  activityName: yup.string().required('First activity name is required'),
  activityType: yup
    .string()
    .oneOf([ResearchActivityType.CORE, ResearchActivityType.SUPPORTING])
    .required('Activity type is required'),
});

const OnboardingProject: React.FC = () => {
  const navigate = useNavigate();
  const { organisationId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projectCreated, setProjectCreated] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [createdProjectName, setCreatedProjectName] = useState('');
  const [createdTaxYear, setCreatedTaxYear] = useState('');

  const getCurrentTaxYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const month = now.getMonth();
    const taxYearStart = month >= 6 ? currentYear : currentYear - 1;
    return `${taxYearStart}-${(taxYearStart + 1).toString().slice(-2)}`;
  };

  const generateTaxYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let i = -1; i <= 2; i++) {
      const year = currentYear + i;
      options.push({
        value: `${year}-${(year + 1).toString().slice(-2)}`,
        label: `${year}-${year + 1} Tax Year`,
      });
    }
    return options;
  };

  const {
    control: projectControl,
    handleSubmit: handleProjectSubmit,
    formState: { errors: projectErrors },
  } = useForm<ProjectFormData>({
    resolver: yupResolver(projectSchema),
    defaultValues: {
      projectName: '',
      taxYear: getCurrentTaxYear(),
    },
  });

  const {
    control: activityControl,
    handleSubmit: handleActivitySubmit,
    formState: { errors: activityErrors },
  } = useForm<ActivityFormData>({
    resolver: yupResolver(activitySchema),
    defaultValues: {
      activityName: '',
      activityType: ResearchActivityType.CORE,
    },
  });

  const onProjectSubmit = async (formData: ProjectFormData) => {
    if (!organisationId) return;

    setLoading(true);
    try {
      const projectResponse = await apiClient.post(`/organisations/${organisationId}/projects`, {
        name: formData.projectName,
        taxYear: formData.taxYear,
        totalBudget: 0,
      });

      const projectData = await projectResponse.json();

      if (projectData.success && projectData.project) {
        setCreatedProjectId(projectData.project.id);
        setCreatedProjectName(formData.projectName);
        setCreatedTaxYear(formData.taxYear);
        setProjectCreated(true);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
    }
  };

  const onActivitySubmit = async (formData: ActivityFormData) => {
    if (!organisationId || !createdProjectId) return;

    setLoading(true);
    try {
      const activityResponse = await apiClient.post(
        `/organisations/${organisationId}/projects/${createdProjectId}/activities`,
        {
          name: formData.activityName,
          description: '',
          type: formData.activityType,
          budgetAmount: 0,
          sortOrder: 0,
        }
      );

      const activityData = await activityResponse.json();

      if (activityData.success) {
        navigate('/onboarding/xero');
      }
    } catch (error) {
      console.error('Error creating activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/xero');
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3, overflow: 'hidden' }}>
      {/* Header */}
      {!projectCreated && (
        <Box mb={4} textAlign="center">
          <ScienceOutlined sx={{ fontSize: 60, mb: 1 }} color="primary" />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {!projectCreated ? 'Create Your First R&D Project' : 'Add Your First Activity'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {!projectCreated
              ? "Let's set up your first research and development project."
              : 'Now create an initial activity for your project.'}
          </Typography>
        </Box>
      )}

      {/* Project Form - slides up when created */}
      <Slide direction="up" in={!projectCreated} mountOnEnter unmountOnExit>
        <form onSubmit={handleProjectSubmit(onProjectSubmit)}>
          <Stack spacing={4}>
            <Stack spacing={3}>
              <Controller
                name="projectName"
                control={projectControl}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<ProjectFormData, 'projectName'>;
                }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Project Name"
                    placeholder="e.g., New Product Development 2025"
                    error={!!projectErrors.projectName}
                    helperText={
                      projectErrors.projectName?.message ||
                      'Give your R&D project a descriptive name'
                    }
                  />
                )}
              />

              <Controller
                name="taxYear"
                control={projectControl}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<ProjectFormData, 'taxYear'>;
                }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Tax Year"
                    error={!!projectErrors.taxYear}
                    helperText={
                      projectErrors.taxYear?.message || 'Select the tax year for this project'
                    }
                  >
                    {generateTaxYearOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'right',
                alignItems: 'center',
                mt: 6,
                gap: 2,
              }}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={handleSkip}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Creating...' : 'Continue'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Slide>

      {/* Project Title Display - slides in when created */}
      <Slide direction="up" in={projectCreated} mountOnEnter>
        <Box>
          <Box sx={{ mb: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <CheckCircleOutlined color="success" fontSize="small" />
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Project Created
              </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {createdProjectName}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Tax Year: {createdTaxYear}
            </Typography>
            <Divider sx={{ mb: 4 }} />
          </Box>
          <Box mb={4} textAlign="center">
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Add Your First Activity
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Now create an initial activity for your project. We'll be allocating transactions to
              these activities later.
            </Typography>
          </Box>

          <form onSubmit={handleActivitySubmit(onActivitySubmit)}>
            <Stack spacing={4}>
              <Controller
                name="activityName"
                control={activityControl}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<ActivityFormData, 'activityName'>;
                }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Activity Name"
                    placeholder="e.g., Software Development"
                    error={!!activityErrors.activityName}
                    helperText={
                      activityErrors.activityName?.message || 'Name your first R&D activity'
                    }
                  />
                )}
              />

              <Controller
                name="activityType"
                control={activityControl}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<ActivityFormData, 'activityType'>;
                }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Activity Type"
                    error={!!activityErrors.activityType}
                    helperText={
                      activityErrors.activityType?.message ||
                      'Core activities are experimental, supporting activities enable the R&D'
                    }
                  >
                    <MenuItem value={ResearchActivityType.CORE}>Core R&D Activity</MenuItem>
                    <MenuItem value={ResearchActivityType.SUPPORTING}>Supporting Activity</MenuItem>
                  </TextField>
                )}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'right',
                  alignItems: 'center',
                  mt: 6,
                  gap: 2,
                }}
              >
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleSkip}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Skip for now
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  disabled={loading}
                  sx={{ minWidth: 200 }}
                >
                  {loading ? 'Creating...' : 'Complete Setup'}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Slide>
    </Box>
  );
};

export default OnboardingProject;
